import { GenericApiResponse } from '../../shared/interfaces/generics';
import { appConfig } from '../../request/app.config';
import { courierService } from '../../request/services/courier.service';
import { RawDestination, RawDestinationType } from '../../shared/interfaces/destination';
import {
  BACKGROUND_NETWORK_REQ_UI_OPTIONS,
  DEFAULT_NETWORK_REQ_OPTIONS
} from '../../request/constants/request-options';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import { AsyncTaskStatusResponse, AsyncTaskResponse } from '../../shared/interfaces/async-task';
import {
  EntityDefaultName,
  TemplateRawForm
} from '../../template-form/interfaces/template-raw-form';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { RawIntegration } from '../../shared/interfaces/integration';

export const DestinationAPI = {
  destinationsURL: appConfig.getDestinationsURL(),
  taskFlowURL: appConfig.getTaskFlowURL(),

  getDestinationTypes(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/types`;

    return courierService.get<GenericApiResponse<RawDestinationType[]>>(requestURL, options);
  },

  getDestinations(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = this.destinationsURL;

    return courierService.get<GenericApiResponse<RawDestination[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  createDestination(config: object, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/create`;

    return courierService.post<GenericApiResponse<RawDestination>>(requestURL, config, options);
  },

  getDestination(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}`;

    return courierService.get<GenericApiResponse<RawDestination>>(requestURL, options);
  },

  validateDestinationName(destinationName: string, config = {}) {
    const requestURL = `${this.destinationsURL}/validate/destination-name`;

    const params = {
      destination_name: destinationName
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.post<GenericApiResponse<{ valid: boolean; reason?: string }>>(
      requestURL,
      params,
      options
    );
  },

  getDestinationIntegrations(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}/integrations`;

    return courierService.get<GenericApiResponse<RawIntegration[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  deleteDestination(destinationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.destinationsURL}/${destinationId}`;

    return courierService.delete<GenericApiResponse<RawDestination>>(requestURL, options);
  },

  getDestinationConfigTemplateByType(
    destinationType: string,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.destinationsURL}/${destinationType}/config-template`;

    return courierService.get<GenericApiResponse<TemplateRawForm>>(requestURL, options);
  },

  getDestinationConfigTemplateWithFormData(
    destinationId: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.destinationsURL}/${destinationId}/config/edit`;

    return courierService.get<GenericApiResponse<TemplateRawForm>>(requestURL, options);
  },

  getDefaultDestinationName(
    destinationType: string,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ): Promise<GenericApiResponse<EntityDefaultName>> {
    const requestURL = `${this.destinationsURL}/${destinationType}/default-name`;

    return courierService.get<GenericApiResponse<EntityDefaultName>>(requestURL, options);
  },

  getTestConnectionTaskIdForNewDestination(
    destinationType: string,
    params: unknown
  ): Promise<GenericApiResponse<AsyncTaskResponse>> {
    const requestUrl = `${this.destinationsURL}/test`;

    const config = { ...(params as unknown as { config: object }).config };

    const payload = {
      destination_type: destinationType,
      config
    };

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, payload);
  },

  getTestConnectionTaskIdForExistingDestination(
    destinationId: number
  ): Promise<GenericApiResponse<AsyncTaskResponse>> {
    const requestUrl = `${this.destinationsURL}/${destinationId}/test`;

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, {});
  },

  getTestConnectionTaskIdForDestinationEdit(destinationId: number, destinationConfig: object) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/edit/test`;

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(
      requestUrl,
      destinationConfig
    );
  },

  getTestConnectionStatus(
    taskFlowId: string
  ): Promise<GenericApiResponse<AsyncTaskStatusResponse>> {
    const requestUrl = `${this.taskFlowURL}/${taskFlowId}/status`;

    return courierService.get<GenericApiResponse<AsyncTaskStatusResponse>>(requestUrl);
  },

  getDestinationDisplayConfigDetails(destinationId: number) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/display-config`;

    return courierService.get<GenericApiResponse<RawEntityDisplayConfig<RawDestinationType>>>(
      requestUrl
    );
  },

  editDestination(destinationId: number, destinationConfig: object) {
    const requestUrl = `${this.destinationsURL}/${destinationId}/config/edit`;

    return courierService.post<GenericApiResponse<RawDestination>>(requestUrl, destinationConfig);
  }
};
